/**
 * OmegaDefensePage — `/omega/defense`.
 *
 * Phase 16 Defense Brain dashboard. Two sections:
 *
 *   1. Emergency Stop panel — current status + activate/resume controls.
 *   2. Threat Feed — last 50 threats at Warning+, auto-refreshed every 15s.
 *
 * Admin controls (emergency-stop, resume) require a founder Ed25519 signature
 * via the same FounderSignDialog pattern used on the Triggers page.
 */

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';
import { AlertTriangle, ChevronRight, Shield, ShieldOff } from 'lucide-react';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { cn } from '@/utils/cn';
import { canonicalJsonStringify } from '@/api/mock/sign';
import { useThreats } from '@/features/omega/dashboard';
import type { ThreatReport } from '@/features/omega/dashboard/data/types';

// ─── API helper ───────────────────────────────────────────────────

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ??
  'http://localhost:3000';

async function founderPost<T>(
  path: string,
  body: unknown,
  founderSig: string,
): Promise<T> {
  const bodyStr = canonicalJsonStringify(body);
  const resp = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, application/problem+json',
      'X-Founder-Signature': founderSig,
    },
    body: bodyStr,
  });
  if (!resp.ok) {
    const text = await resp.text();
    let detail = text;
    try {
      detail = (JSON.parse(text) as { detail?: string }).detail ?? text;
    } catch {}
    throw new Error(detail || `HTTP ${resp.status}`);
  }
  return resp.json() as Promise<T>;
}

// ─── Page ────────────────────────────────────────────────────────

export function OmegaDefensePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  return (
    <div className="flex flex-col gap-6 pb-8">
      <header className="flex items-center gap-2">
        <Link to="/omega" className="text-xs text-ink-subtle hover:text-ink">OMEGA</Link>
        <ChevronRight size={12} className="text-ink-muted" aria-hidden />
        <span className="text-xs font-medium text-ink">Defense</span>
      </header>
      <h1 className="sr-only">Defense Brain</h1>

      <EmergencyStopPanel />
      <ThreatFeed />
    </div>
  );
}

// ─── EmergencyStopPanel ───────────────────────────────────────────

interface StopStatus {
  emergency_stop_active: boolean;
  activated_by: string | null;
  activated_at_iso: string | null;
}

function EmergencyStopPanel() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'stop' | 'resume' | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();

  const { data } = useQuery<StopStatus>({
    queryKey: ['defense', 'status'],
    queryFn: () =>
      fetch(`${API_BASE}/api/v1/defense/status`, {
        headers: { Accept: 'application/json' },
      })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<StopStatus>;
        })
        .catch(() => ({ emergency_stop_active: false, activated_by: null, activated_at_iso: null })),
    refetchInterval: 10_000,
  });

  const active = data?.emergency_stop_active ?? false;

  const handleSign = async (sig: string, nonce: string) => {
    setBusy(true);
    setErr(undefined);
    try {
      if (dialog === 'stop') {
        await founderPost('/api/v1/admin/defense/emergency-stop', { nonce }, sig);
      } else {
        await founderPost('/api/v1/admin/defense/resume', { nonce }, sig);
      }
      await qc.invalidateQueries({ queryKey: ['defense'] });
      setDialog(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section
        aria-label="Emergency stop"
        className={cn(
          'rounded-xl border p-5',
          active
            ? 'border-danger/50 bg-danger/5'
            : 'border-border bg-surface',
        )}
      >
        <div className="flex items-center gap-3">
          {active ? (
            <ShieldOff size={20} className="text-danger" aria-hidden />
          ) : (
            <Shield size={20} className="text-success" aria-hidden />
          )}
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-ink">
              {active ? 'Emergency Stop Active' : 'Defense Status: Normal'}
            </h2>
            {active && data?.activated_by && (
              <p className="mt-0.5 text-xs text-ink-muted">
                Activated by {data.activated_by}
                {data.activated_at_iso
                  ? ` · ${new Date(data.activated_at_iso).toLocaleString()}`
                  : ''}
              </p>
            )}
            {!active && (
              <p className="mt-0.5 text-xs text-ink-muted">
                All trigger fires are operational. Emergency stop is not active.
              </p>
            )}
          </div>
          {!active ? (
            <button
              type="button"
              onClick={() => { setErr(undefined); setDialog('stop'); }}
              className="rounded-md border border-danger/50 bg-danger/10 px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/20"
            >
              Activate Emergency Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { setErr(undefined); setDialog('resume'); }}
              className="rounded-md border border-success/50 bg-success/10 px-3 py-1.5 text-xs font-semibold text-success hover:bg-success/20"
            >
              Resume Operations
            </button>
          )}
        </div>
        {active && (
          <div className="mt-3 rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
            All trigger fires are blocked. POST /admin/defense/resume to restore operations.
          </div>
        )}
      </section>

      {dialog && (
        <FounderSignDialog
          label={dialog === 'stop' ? 'Activate Emergency Stop' : 'Resume Operations'}
          action={dialog}
          isPending={busy}
          {...(err ? { error: err } : {})}
          onSign={handleSign}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
}

// ─── FounderSignDialog ────────────────────────────────────────────

function FounderSignDialog({
  label,
  action,
  isPending,
  error,
  onSign,
  onClose,
}: {
  label: string;
  action: 'stop' | 'resume';
  isPending: boolean;
  error?: string;
  onSign: (sig: string, nonce: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const nonce = crypto.randomUUID();
  const body = action === 'stop' ? { nonce } : { nonce };
  const bodyStr = canonicalJsonStringify(body);
  const payloadBytes = new TextEncoder().encode(bodyStr);
  const hashHex = bytesToHex(sha256(payloadBytes));
  const hashString = `sha256:${hashHex}`;

  const [sig, setSig] = useState('');
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard.writeText(hashString);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Founder sign dialog"
    >
      <div className="flex w-full max-w-lg flex-col gap-5 rounded-xl border border-border bg-surface p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">{label}</h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>

        <p className="text-sm text-ink-muted">
          Sign the hash below with your founder Ed25519 key and paste the result.
        </p>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Canonical payload (JCS JSON)
          </label>
          <pre className="max-h-24 overflow-auto rounded-md border border-border bg-canvas px-3 py-2 font-mono text-[11px] text-ink-muted">
            {bodyStr}
          </pre>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Hash to sign
          </label>
          <div className="flex items-center gap-2 rounded-md border border-border bg-canvas px-3 py-2">
            <code className="flex-1 break-all font-mono text-[11px] text-ink">{hashString}</code>
            <button
              type="button"
              onClick={copy}
              className="shrink-0 rounded border border-border px-2 py-0.5 text-[10px] text-ink-muted hover:text-ink"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="defense-sig-input" className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Signature (ed25519:&lt;base64&gt;)
          </label>
          <input
            id="defense-sig-input"
            type="text"
            placeholder="ed25519:<base64-signature>"
            value={sig}
            onChange={(e) => setSig(e.target.value)}
            className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-[11px] text-ink placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-spark"
          />
        </div>

        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-border px-4 py-2 text-sm text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSign(sig.trim(), nonce)}
            disabled={!sig.trim() || isPending}
            className={cn(
              'flex-1 rounded-md px-4 py-2 text-sm font-semibold',
              action === 'stop'
                ? 'bg-danger text-white hover:bg-danger/90'
                : 'bg-success text-white hover:bg-success/90',
              (!sig.trim() || isPending) && 'cursor-not-allowed opacity-50',
            )}
          >
            {isPending ? 'Submitting…' : label}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ThreatFeed ───────────────────────────────────────────────────

function ThreatFeed() {
  const { data, isPending, isError } = useThreats();
  const threats = data?.threats ?? [];

  return (
    <section aria-label="Threat feed" className="rounded-xl border border-border bg-surface">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <AlertTriangle size={14} className="text-ink-muted" aria-hidden />
        <h2 className="flex-1 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Threat Feed
        </h2>
        <span className="font-mono text-[10px] text-ink-muted">
          last {data?.total ?? 0} threats
        </span>
      </div>

      <div className="flex flex-col gap-1 p-3">
        {isPending && (
          <div className="flex h-16 items-center justify-center text-xs text-ink-subtle">
            Loading…
          </div>
        )}
        {isError && (
          <div className="flex h-16 items-center justify-center text-xs text-danger">
            Failed to load threat feed.
          </div>
        )}
        {!isPending && !isError && threats.length === 0 && (
          <div className="flex h-16 items-center justify-center text-xs text-ink-subtle">
            No threats detected — defense is nominal.
          </div>
        )}
        {threats.map((t) => (
          <ThreatRow key={t.id} threat={t} />
        ))}
      </div>
    </section>
  );
}

function ThreatRow({ threat: t }: { threat: ThreatReport }) {
  const severityStyle = {
    critical: {
      badge: 'border-danger/50 bg-danger/10 text-danger',
      dot: 'bg-danger',
    },
    warning: {
      badge: 'border-warning/50 bg-warning/10 text-warning',
      dot: 'bg-warning',
    },
    info: {
      badge: 'border-border bg-canvas text-ink-subtle',
      dot: 'bg-ink-subtle',
    },
  }[t.severity] ?? { badge: 'border-border bg-canvas text-ink-subtle', dot: 'bg-ink-subtle' };

  const detectedAt = new Date(t.detected_at_iso);
  const diff = Date.now() - detectedAt.getTime();
  const age =
    diff < 60_000
      ? `${Math.round(diff / 1000)}s ago`
      : diff < 3_600_000
        ? `${Math.round(diff / 60_000)}m ago`
        : `${Math.round(diff / 3_600_000)}h ago`;

  return (
    <div className="flex items-start gap-2.5 rounded-md px-2 py-2 hover:bg-canvas">
      <span
        className={cn(
          'mt-0.5 shrink-0 rounded border px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase',
          severityStyle.badge,
        )}
      >
        {t.severity}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[11px] text-ink">{t.kind}</div>
        <div className="mt-0.5 text-[10px] text-ink-muted">{t.description}</div>
        {t.recommended_action && (
          <div className="mt-0.5 text-[10px] italic text-ink-subtle">
            → {t.recommended_action}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="text-[10px] tabular-nums text-ink-muted">{age}</span>
        <span className={cn('h-1.5 w-1.5 rounded-full', severityStyle.dot)} aria-hidden />
      </div>
    </div>
  );
}
